import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
  Portal,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { ReactElement, useState } from 'react';
import { usePopper } from '../../hooks/usePopper';
import { classNames } from '../../utils';
import Badge from './Badge';
import Loader from './Loader';

export interface OptionRenderProps {
  active: boolean;
  selected: boolean;
  disabled: boolean;
}

interface Props<T> {
  title?: string;
  placeholder?: string;
  selected?: T[];
  onChange: (_: T[]) => void;
  options: T[];
  getValue?: (_: T) => string;
  renderItem?: (_: T) => ReactElement;
  getOptionId?: (_: T) => string;
  renderOption?: (_: T, __: OptionRenderProps) => ReactElement;
  onInputChange?: (_: string) => void;
  filter?: (filterValue: string, options: T[]) => T[];
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  filterQuery?: (query: string) => string;
}

export default function ChipsAutocomplete<T>({
  title,
  placeholder,
  selected,
  onChange,
  options,
  getValue,
  renderItem,
  getOptionId,
  renderOption,
  onInputChange,
  filter,
  loading,
  disabled,
  className,
  filterQuery,
}: Props<T>) {
  const [trigger, container, triggerRef] = usePopper({
    placement: 'bottom-end',
    strategy: 'fixed',

    modifiers: [
      {
        name: 'matchWidth',
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  const [query, setQuery] = useState('');

  const _getValue = getValue || ((a) => `${a}`);
  const _renderItem = renderItem || ((opt) => <>{_getValue(opt)}</>);
  const _getOptionId = getOptionId || ((a) => `${a}`);
  const _renderOption =
    renderOption ||
    ((opt, args) =>
      (
        <AutocompleteOption
          option={_renderItem(opt)}
          active={args.active}
          selected={args.selected}
          disabled={args.disabled}
        />
      ) as ReactElement);
  const _filter =
    filter ||
    ((query, options) =>
      options.filter((option) => {
        return _getValue(option).toLowerCase().includes(query.toLowerCase());
      }));

  const filtered = query === '' ? options : _filter(query, options);

  return (
    <Combobox
      multiple
      as="div"
      value={selected}
      disabled={disabled}
      onChange={onChange}
      className={className}
      ref={trigger}
    >
      {title ? (
        <Label className="block text-sm font-medium leading-6 text-gray-900">
          {title}
        </Label>
      ) : (
        <></>
      )}
      <div
        className={classNames(
          'relative mt-2 flex flex-row flex-wrap gap-2',
          'w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
          disabled && 'opacity-50',
        )}
        onClick={() => (triggerRef.current as HTMLInputElement)?.focus()}
      >
        {(selected?.length || 0) > 0 ? (
          <div className="flex flex-row flex-wrap items-center gap-1">
            {selected?.map((item) => (
              <Badge
                hideDot
                key={_getOptionId(item)}
                title={_renderItem(item)}
                showDeleteButton
                onDelete={
                  disabled
                    ? undefined
                    : () => {
                        onChange(
                          selected.filter(
                            (a) => _getOptionId(a) !== _getOptionId(item),
                          ),
                        );
                      }
                }
              />
            ))}
          </div>
        ) : (
          <></>
        )}
        <ComboboxInput
          multiple
          className={classNames(
            'min-w-6 flex-1 border-0 p-0 ring-0 focus:ring-0',
          )}
          value={query}
          placeholder={placeholder}
          onChange={(event) => {
            const value = filterQuery
              ? filterQuery(event.target.value)
              : event.target.value;
            setQuery(value);
            onInputChange?.(value);
          }}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          {loading ? <Loader size={4} className="mb-1 me-1" /> : <></>}
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>

        <Portal>
          {filtered.length > 0 && (
            <ComboboxOptions
              ref={container}
              className="z-10 max-h-56 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              {filtered.map((option) => (
                <ComboboxOption
                  key={_getOptionId(option)}
                  value={option}
                  className={({ active }) =>
                    classNames(
                      'relative cursor-default select-none py-2 pl-3 pr-9',
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    )
                  }
                >
                  {(args) => _renderOption(option, args)}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          )}
        </Portal>
      </div>
    </Combobox>
  );
}

interface AutocompleteOptionProps extends OptionRenderProps {
  option: ReactElement;
}

function AutocompleteOption({
  option,
  selected,
  active,
}: AutocompleteOptionProps) {
  return (
    <>
      <div className="flex items-center">
        <span
          className={classNames('ml-3 truncate', selected && 'font-semibold')}
        >
          {option}
        </span>
      </div>

      {selected && (
        <span
          className={classNames(
            'absolute inset-y-0 right-0 flex items-center pr-4',
            active ? 'text-white' : 'text-indigo-600',
          )}
        >
          <CheckIcon className="h-5 w-5" aria-hidden="true" />
        </span>
      )}
    </>
  );
}
